/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React from 'react';
import { Dialog, FormControl, FormControlLabel, makeStyles, Typography, AppBar, Divider } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import Container from '@material-ui/core/Container';
import Box from '@material-ui/core/Box';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogActions from '@material-ui/core/DialogActions';
import { Controller } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import SendIcon from '@material-ui/icons/Send';
import SelectWithSearch from 'components/common/SelectWithSearch';
import DOMPurify from 'dompurify';
import DialogStatus from '../../layout/dialog/DialogStatus';
import { useUserAccounts } from '../../redux/features/user/userAccounts/useUserAccounts';
import Button from '../../components/common/Button';
import useRequestReceipt from './useRequestReceipt';
import { PreviewHeader } from '../../components/preview';
import CustomCheckbox from '../../components/CustomCheckbox';
import { RoutePath } from '../../shared/enums/RoutePath';
import Breadcrumb from '../../components/common/Breadcrumb';
import { dataTestId } from '../../shared/utils/versionUtils';

const useStyles = makeStyles(theme => ({
  content: {
    flexGrow: 1,
    padding: theme.spacing(3),
  },
  divider: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(5),
  },
  header: {
    margin: '1rem 0 2.625rem 0',
  },
  subheader: {
    margin: '0 0 1rem 0',
  },
  subsection: {
    margin: '0 0 3rem 0',
  },
  staticText: {
    marginBottom: '3rem',
  },
  formContainer: {
    margin: 0,
    padding: 0,
  },
  sectionBox: {
    backgroundColor: theme.palette.elementBackground,
    padding: '2.625rem 4.063rem',
  },
  emailFieldContainer: {
    maxWidth: '25rem',
  },
  emailField: {
    display: 'flex',
    flex: 1,
  },
  checkboxContainer: {
    margin: '3rem 0',
  },
  bottomBar: {
    position: 'sticky',
    padding: '.25rem',
    top: 'unset',
    bottom: 0,
    left: '-2rem',
    right: 0,
    justifyContent: 'center',
    color: theme.palette.footerText,
    backgroundColor: theme.palette.footerBackground,
    opacity: 0.95,
    [theme.breakpoints.between('xs', 'md')]: {
      width: '100%',
    },
    [theme.breakpoints.between('md', 'lg')]: {
      width: '100%',
    },
    [theme.breakpoints.between('lg', 'xl2')]: {
      width: '100%',
    },
    [theme.breakpoints.up('xl2')]: {
      width: '100%',
      paddingLeft: '3.5%',
      paddingRight: '3.5%',
    },
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    alignItems: 'center',
    padding: '1.188rem 4.25rem 1.188rem 0',
  },
  button: {
    marginRight: '1.5rem',
  },
  gdprFragment: {
    display: 'flex',
  },
  gdprLink: {
    zIndex: 1,
  },
}));

export default function RequestReceipt() {
  const classes = useStyles();
  const history = useHistory();
  const { t } = useTranslation();
  const { user } = useUserAccounts();
  const {
    confirmRequest,
    control,
    dialogError,
    dialogOpened,
    emailRules,
    formState,
    handleGoToOverview,
    handleSendAnother,
    handleSubmit,
    validateEmail,
    downloadReceiptFile,
    submission,
    currentProcedure,
  } = useRequestReceipt();

  if (!user) return <></>;

  return (
    <>
      <div className="wideContainer">
        <div className={classes.content} {...dataTestId({ value: 'request-receipt-page' })}>
          <Breadcrumb className="breadcrumbs" currentBreadcrumb={t('messages.submission.request.receipt')} />

          <PreviewHeader
            className={classes.header}
            submission={submission}
            headerTitle={t('messages.submission.request.receipt')}
          />
          <Typography className={classes.staticText}>{t('messages.submission.request.receipt.description')}</Typography>

          <Button color="primary" onClick={() => downloadReceiptFile()}>
            {t('messages.submission.request.receipt.download')}
          </Button>

          <Dialog open={dialogOpened} fullWidth maxWidth="md">
            <DialogTitle>
              {!dialogError && (
                <>
                  <DialogStatus variant="success" />
                  <Typography variant="h2" component="span">
                    {t('messages.submission.request.receipt.success')}
                  </Typography>
                </>
              )}
              {dialogError && (
                <>
                  <DialogStatus variant="warning" />
                  <Typography variant="h2" component="span">
                    {t('messages.submission.request.receipt.error')}
                  </Typography>
                </>
              )}
            </DialogTitle>
            <DialogActions>
              <Button color="default" onClick={() => handleSendAnother()}>
                {t('messages.submission.request.receipt.another')}
              </Button>
              <Button color="primary" onClick={() => handleGoToOverview()}>
                {t('messages.submission.request.receipt.close')}
              </Button>
            </DialogActions>
          </Dialog>

          <Divider className={classes.divider} />
          <Container className={classes.formContainer} maxWidth="md">
            <form onSubmit={handleSubmit(confirmRequest)} id="requestForm">
              <Box className={classes.sectionBox}>
                <Typography className={classes.subheader} variant="h3">
                  {t('messages.submission.request.receipt.subheader')}
                </Typography>
                <Typography className={classes.subsection}>
                  {t('messages.submission.request.receipt.subsection')}
                </Typography>

                <Controller
                  control={control}
                  name="email"
                  rules={emailRules}
                  defaultValue={[user.emailAddress]}
                  render={({ onChange, name }) => (
                    <Box className={classes.emailFieldContainer}>
                      <SelectWithSearch
                        name={name}
                        defaultValue={[user.emailAddress]}
                        customValidation={validateEmail}
                        multiple={false}
                        placeholder={t('user-management.user-email')}
                        enableAutocomplete={false}
                        onChange={onChange}
                      />
                    </Box>
                  )}
                />
              </Box>

              <div className={classes.checkboxContainer}>
                <Controller
                  name="dataProtectionAcceptance"
                  control={control}
                  rules={{ required: true }}
                  defaultValue={false}
                  render={({ onChange }) => (
                    <FormControl>
                      <FormControlLabel
                        label={(
                          <div className={classes.gdprFragment}>
                            <Trans i18nKey="messages.submission.request.receipt.gdpr" />
                            <Typography
                              className={classes.gdprLink}
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                  `&nbsp;${t('messages.submission.request.receipt.gdpr.url')}`,
                                ),
                              }}
                            />
                          </div>
                        )}
                        control={<CustomCheckbox onChange={e => onChange(e.target.checked)} />}
                      />
                    </FormControl>
                  )}
                />
              </div>
            </form>
          </Container>
        </div>
      </div>
      <AppBar component="div" position="sticky" elevation={0} className={classes.bottomBar}>
        <div className={classes.toolbar}>
          <Button
            id="cancelRequestReceipt"
            className={classes.button}
            color="secondary"
            onClick={() =>
              history.replace(
                RoutePath.generateRoutePath(RoutePath.PROCEDURE_SEARCH, {
                  procedure: currentProcedure.id,
                  initialSubmissionProcedure: submission?.initialSubmissionProcedure,
                }),
              )
            }
          >
            {t('actions.cancel')}
          </Button>
          <Button
            id="confirmRequestReceipt"
            className={classes.button}
            type="submit"
            form="requestForm"
            color="primary"
            startIcon={<SendIcon disabled={!formState.isValid} />}
            disabled={!formState.isValid}
          >
            {t('actions.submission.request.receipt.button')}
          </Button>
        </div>
      </AppBar>
    </>
  );
}
